import { Button, Box, Alert } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React, { useRef, useState, useEffect } from "react";
import { generateConflictReport } from "../api/report.js";
import { ONE_YEAR_IN_PAST } from "../util/dateHelper.js";
import { convert_Luxon_To_DateFns, set_time_to_end_of_day } from "../util/dateTimeFormatter.js";
import {
  ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE,
  ARE_YOU_SURE_YOU_WANT_TO_GENERATE_THE_REPORT_MESSAGE,
  REPORT_GENERATION_WARNING_MESSAGE,
  WARNING_ALERT_TITLE,
  SCHEDULE_CONFLICT_REPORT_ALERT_MESSAGE,
} from "../util/messageConstants.js";
import { validateReportShiftDate } from "../util/reportInputValidation.js";
import GenerateReportDownloadDialog from "./GenerateReportDownloadDialog.jsx";
import ConfirmationDialog from "./fundamentals/ConfirmationDialog.jsx";
import StarsDatePicker from "./fundamentals/StarsDatePicker.jsx";
import { CANCEL_WARNING_MESSAGE_ACTION_TYPE } from "../util/constants.js";
import {
  validateConflictReportEndDateFilter,
  validateConflictReportStartDateFilter,
} from "../util/starsConflictReportInputValidation.js";
import { getTimeZoneFromSwaStation } from "../util/timeZoneService";

const useStyles = makeStyles((theme) => ({
  input: {
    width: "348px",
  },
  buttonContainer: {
    marginTop: "20px",
  },
  link: {
    display: "none",
  },
  control: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const StarsConflictReport = (props) => {
  const classes = useStyles();
  const [startDate, setStartDate] = useState(null);
  const [startDateErrors, setStartDateErrors] = useState("");
  const [endDate, setEndDate] = useState(null);
  const [endDateErrors, setEndDateErrors] = useState("");
  const [isCancelConfirmDialogOpen, setIsCancelConfirmDialogOpen] = useState(false);
  const [isGenerateConfirmDialogOpen, setIsGenerateConfirmDialogOpen] = useState(false);
  const [isGenerateDownloadDialogOpen, setIsGenerateDownloadDialogOpen] = useState(false);
  const [isReportDownloading, setIsReportDownloading] = useState(false);
  const [isReportDownloadError, setIsReportDownloadError] = useState(false);
  const [downloadedFile, setDownloadedFile] = useState(null);
  const [responseMessage, setResponseMessage] = useState("");

  useEffect(() => {
    if (startDate) {
      const startDateUpdated = startDate.setZone(getTimeZoneFromSwaStation(props.station), {
        keepLocalTime: true,
      });

      setStartDate(startDateUpdated);
    }

    if (endDate) {
      const endDateUpdated = endDate.setZone(getTimeZoneFromSwaStation(props.station), {
        keepLocalTime: true,
      });

      setEndDate(endDateUpdated);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.station]);

  useEffect(() => {
    validateStartDateWithEndDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  const validateStartDateWithEndDate = () => {
    if (startDate !== null && endDate !== null) {
      const newStartDateFilterErrors = validateConflictReportStartDateFilter(startDate, endDate, props.station);
      if (newStartDateFilterErrors !== "") {
        setStartDateErrors(newStartDateFilterErrors);
      } else {
        setStartDateErrors("");
        setEndDateErrors("");
      }
      return newStartDateFilterErrors;
    } else {
      setStartDateErrors("");
      return "";
    }
  };

  useEffect(() => {
    validateEndDateWithStartDate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate]);

  const validateEndDateWithStartDate = () => {
    if (startDate !== null && endDate !== null) {
      const newEndDateFilterErrors = validateConflictReportEndDateFilter(endDate, startDate, props.station, false);
      if (newEndDateFilterErrors !== "") {
        setEndDateErrors(newEndDateFilterErrors);
      } else {
        setEndDateErrors("");
        setStartDateErrors("");
      }
      return newEndDateFilterErrors;
    } else {
      setEndDateErrors("");
      return "";
    }
  };

  const linkRef = useRef();

  const resetState = () => {
    setIsReportDownloading(false);
    setIsReportDownloadError(false);
    setResponseMessage("");
    setDownloadedFile(null);
  };

  const isFormValidationSuccess = () => {
    const startDateFormValidationErrors = validateReportShiftDate(startDate);
    setStartDateErrors(startDateFormValidationErrors);

    let startDateWithEndDateFormValidationErrors = "";
    if (startDateFormValidationErrors === "") {
      startDateWithEndDateFormValidationErrors = validateStartDateWithEndDate();
      setStartDateErrors(startDateWithEndDateFormValidationErrors);
    }

    const endDateFormValidationErrors = validateReportShiftDate(endDate);
    setEndDateErrors(endDateFormValidationErrors);

    let endDateWithStartDateFormValidationErrors = "";
    if (endDateFormValidationErrors === "") {
      endDateWithStartDateFormValidationErrors = validateEndDateWithStartDate();
      setEndDateErrors(endDateWithStartDateFormValidationErrors);
    }

    return (
      startDateFormValidationErrors === "" &&
      startDateWithEndDateFormValidationErrors === "" &&
      endDateFormValidationErrors === "" &&
      endDateWithStartDateFormValidationErrors === ""
    );
  };

  const buildGenerateReportPayload = () => {
    return {
      station: props.station,
      startDate: startDate.toMillis().toString(),
      endDate: endDate.toMillis().toString(),
    };
  };

  const handleGenerateReport = () => {
    resetState();
    setIsReportDownloading(true);
    generateConflictReport(buildGenerateReportPayload())
      .then((res) => res.json())
      .then(async (result) => {
        if (result.message) {
          setResponseMessage(result.message);
        } else {
          setDownloadedFile(result);
        }
        setIsReportDownloading(false);
      })
      .catch((err) => {
        setIsReportDownloadError(true);
        setIsReportDownloading(false);
      });
  };

  const handleCancelButtonClicked = () => {
    setIsCancelConfirmDialogOpen(true);
  };

  const handleCancelDialogNoButtonClicked = () => {
    setIsCancelConfirmDialogOpen(false);
  };

  const handleCancelDialogYesButtonClicked = () => {
    setIsCancelConfirmDialogOpen(false);
    props.onResetState();
  };

  const handleGenerateButtonClicked = () => {
    if (isFormValidationSuccess()) {
      setIsGenerateConfirmDialogOpen(true);
    }
  };

  const handleGenerateDialogNoButtonClicked = () => {
    setIsGenerateConfirmDialogOpen(false);
  };

  const handleGenerateDialogYesButtonClicked = () => {
    setIsGenerateConfirmDialogOpen(false);
    setIsGenerateDownloadDialogOpen(true);
    handleGenerateReport();
  };

  const handleDownloadDialogClose = () => {
    setIsGenerateDownloadDialogOpen(false);
  };

  const handleDownloadDialogDownloadButtonClicked = () => {
    const a = linkRef.current;
    a.download = downloadedFile.fileName;
    a.href = downloadedFile.downloadUrl;
    a.click();
    a.href = "";
    setIsGenerateDownloadDialogOpen(false);
    resetState();
    props.onResetState();
  };

  const handleStartDateChange = (date) => {
    let inputErrors = validateReportShiftDate(date);
    if (inputErrors === "") {
      setStartDate(date);
      setStartDateErrors("");
    } else {
      setStartDateErrors(inputErrors);
    }
  };

  const handleEndDateChange = (date) => {
    const endOfDay = set_time_to_end_of_day(date);
    let inputErrors = validateReportShiftDate(endOfDay);
    if (inputErrors === "") {
      setEndDate(endOfDay);
      setEndDateErrors("");
    } else {
      setEndDateErrors(inputErrors);
    }
  };

  return (
    <Grid item data-testid="stars-conflict-report">
      <Grid item xs={12}>
        <FormControl className={classes.control}>
          <StarsDatePicker
            label="Start Date"
            date={startDate}
            onChange={handleStartDateChange}
            dateErrors={startDateErrors}
            className={classes.input}
          />
        </FormControl>
        <FormControl className={classes.control}>
          <StarsDatePicker
            label="End Date"
            date={endDate}
            onChange={handleEndDateChange}
            dateErrors={endDateErrors}
            minDate={convert_Luxon_To_DateFns(ONE_YEAR_IN_PAST(props.station))}
            className={classes.input}
          />
        </FormControl>
      </Grid>
      <Grid container justifyContent="center">
        <Box width={520} style={{ padding: 15 }}>
          <Alert severity="info"> {SCHEDULE_CONFLICT_REPORT_ALERT_MESSAGE}</Alert>
        </Box>
      </Grid>
      <Grid container item justifyContent="flex-end" spacing={2} xs={12} className={classes.buttonContainer}>
        <Grid item>
          <Button
            className={classes.button}
            variant="outlined"
            color="primary"
            size="large"
            onClick={handleCancelButtonClicked}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item>
          <Button
            className={classes.button}
            variant="contained"
            color="primary"
            onClick={handleGenerateButtonClicked}
            size="large"
          >
            Generate
          </Button>
        </Grid>
      </Grid>
      {isCancelConfirmDialogOpen && (
        <ConfirmationDialog
          message={ARE_YOU_SURE_YOU_WANT_TO_CANCEL_DIALOG_MESSAGE(CANCEL_WARNING_MESSAGE_ACTION_TYPE.SELECTIONS)}
          isDialogOpen={isCancelConfirmDialogOpen}
          handleNoButtonClicked={handleCancelDialogNoButtonClicked}
          handleYesButtonClicked={handleCancelDialogYesButtonClicked}
        />
      )}
      {isGenerateConfirmDialogOpen && (
        <ConfirmationDialog
          message={ARE_YOU_SURE_YOU_WANT_TO_GENERATE_THE_REPORT_MESSAGE}
          hasWarning={true}
          warningMessage={REPORT_GENERATION_WARNING_MESSAGE}
          isDialogOpen={isGenerateConfirmDialogOpen}
          handleNoButtonClicked={handleGenerateDialogNoButtonClicked}
          handleYesButtonClicked={handleGenerateDialogYesButtonClicked}
          warningTitle={WARNING_ALERT_TITLE}
        />
      )}
      {isGenerateDownloadDialogOpen && (
        <>
          <GenerateReportDownloadDialog
            isDownloading={isReportDownloading}
            isError={isReportDownloadError}
            isDialogOpen={isGenerateDownloadDialogOpen}
            onClose={handleDownloadDialogClose}
            onDownload={handleDownloadDialogDownloadButtonClicked}
            onRetry={handleGenerateReport}
            responseMessage={responseMessage}
          />
          <a className={classes.link} ref={linkRef} href="/">
            Download Report
          </a>
        </>
      )}
    </Grid>
  );
};

StarsConflictReport.propTypes = {
  station: PropTypes.string.isRequired,
  onResetState: PropTypes.func.isRequired,
};

export default StarsConflictReport;
